<template>
  <div :class="[embedded ? 'map-kit-monitor-panel map-kit-monitor-panel--embedded' : 'map-kit-panel map-kit-panel--right map-kit-monitor-panel']">
    <div v-if="!embedded" class="map-kit-panel-header">
      <span>{{ $t('monitor.title') }}</span>
      <div class="map-kit-panel-header-icons">
        <Button
          icon="fas fa-xmark"
          size="small"
          text
          severity="secondary"
          @click="$emit('close')"
        />
      </div>
    </div>

    <div :class="embedded ? 'map-kit-monitor-panel-body' : 'map-kit-panel-body'">
      <input
        ref="fileInput"
        type="file"
        accept=".json,.geojson"
        multiple
        style="display: none"
        @change="onFileChange"
      />

      <!-- Drop zone: prominent when empty, compact when areas exist -->
      <div
        :class="['map-kit-monitor-dropzone', {
          'map-kit-monitor-dropzone--empty': !areas.length,
          'map-kit-monitor-dropzone--dragover': isDragOver,
        }]"
        @click="openFilePicker"
        @dragover.prevent="onDragOver"
        @dragenter.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="map-kit-monitor-dropzone-icon">
          <FontAwesomeIcon :icon="isDragOver ? 'fas fa-file-import' : 'fas fa-cloud-arrow-up'" />
        </div>
        <p class="map-kit-monitor-dropzone-title">
          {{ isDragOver ? $t('monitor.dropFiles') : $t('monitor.dropzoneTitle') }}
        </p>
        <p v-if="!isDragOver" class="map-kit-monitor-dropzone-subtitle">
          {{ $t('monitor.dropzoneSubtitle') }}
        </p>
        <p v-if="!isDragOver" class="map-kit-monitor-dropzone-hint">
          {{ $t('monitor.dropzoneHint') }}
        </p>
      </div>

      <p v-if="status === 'validating'" class="map-kit-monitor-dropzone-notice map-kit-monitor-dropzone-notice--loading">
        <FontAwesomeIcon icon="fas fa-circle-notch" spin />
        <span>{{ noticeMessage ?? 'Validating AOI...' }}</span>
      </p>

      <p v-else-if="status === 'error' && errorMessage" class="map-kit-monitor-dropzone-error">
        {{ errorMessage }}
      </p>

      <p v-else-if="noticeMessage" class="map-kit-monitor-dropzone-notice">
        {{ noticeMessage }}
      </p>

      <DataTable
        v-if="areas.length"
        v-model:selection="selectedAreas"
        :value="areas"
        data-key="id"
        paginator
        scrollable
        scroll-height="flex"
        :rows="10"
        size="small"
        sort-field="queried_at"
        :sort-order="-1"
        :meta-key-selection="false"
        class="map-kit-monitor-table"
      >
        <!-- Selection column -->
        <Column selection-mode="multiple" header-style="width: 3rem" />

        <!-- Name (click to rename) -->
        <Column field="name" :header="$t('monitor.name')" sortable>
          <template #body="{ data }">
            <div class="map-kit-monitor-name-cell">
              <template v-if="renamingAreaId === data.id">
                <input
                  ref="renameInput"
                  v-model="renameValue"
                  class="map-kit-monitor-rename-input"
                  @keydown.enter="confirmRename(data.id)"
                  @keydown.escape="cancelRename"
                  @blur="confirmRename(data.id)"
                />
              </template>
              <template v-else>
                <span
                  class="map-kit-monitor-name-text"
                  @click="panToArea(data)"
                  @dblclick="startRename(data)"
                  v-tooltip.top="$t('monitor.clickToPan')"
                >{{ data.name }}</span>
              </template>
            </div>
          </template>
        </Column>

        <Column field="coverage_summary" :header="$t('monitor.overpasses')">
          <template #body="{ data }">
            <div class="map-kit-monitor-coverage-cell">
              <span v-if="isAreaComputing(data.id)" class="map-kit-monitor-coverage-loading">
                <span class="map-kit-monitor-coverage-dots">...</span>
              </span>
              <span v-else class="map-kit-monitor-coverage-text">
                {{ getOverpassCount(data) ?? '—' }}
              </span>
            </div>
          </template>
        </Column>

        <!-- Calculated date -->
        <Column field="queried_at" :header="$t('monitor.calculated')" sortable>
          <template #body="{ data }">
            <span v-if="isAreaComputing(data.id)" class="map-kit-monitor-status-subtitle">
              {{ $t('monitor.computing') }}
            </span>
            <span v-else>
              {{ formatDateTime(data.queried_at || data.modified_at) }}
            </span>
          </template>
        </Column>

        <!-- Area km² -->
        <Column field="area_km2" :header="$t('monitor.area')" sortable>
          <template #body="{ data }">
            {{ Math.round(data.area_km2).toLocaleString() }} km²
          </template>
        </Column>

        <!-- Paginator start slot — bulk actions -->
        <template #paginatorstart>
          <div v-if="selectedAreas.length" class="map-kit-monitor-bulk">
            <span class="map-kit-monitor-bulk-count">{{ $t('monitor.selected', { count: selectedAreas.length }) }}</span>
            <Button
              :label="$t('monitor.export')"
              icon="fas fa-file-export"
              size="small"
              text
              severity="secondary"
              @click="exportSelected"
            />
            <Button
              :label="$t('monitor.delete')"
              icon="fas fa-trash"
              size="small"
              text
              severity="danger"
              @click="deleteSelected"
            />
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import GeoJSON from 'ol/format/GeoJSON';
import { useAoiStore } from '@/composables/useAoiStore';
import { useMonitoredAreasStore, type MonitoredArea } from '@/composables/useMonitoredAreasStore';
import { useMap } from '@/composables/useMap';
import { useFilterStore } from '@/composables/useFilterStore';
import { useDateTimeSettings } from '@/composables/useDateTimeSettings';

type MonitorRow = Readonly<MonitoredArea>;

withDefaults(
  defineProps<{
    embedded?: boolean;
  }>(),
  {
    embedded: false,
  },
);

defineEmits<{ (e: 'close'): void }>();

const isSyncingSelection = ref(false);

const { areas, isHydrated, addAreaFromAoi, setAreaCoverage, setVisibleAreaIds, removeArea, renameArea } = useMonitoredAreasStore();
const { status, errorMessage, noticeMessage, submitCoverage, loadAoiFile, clearResult } = useAoiStore();
const fileInput = ref<HTMLInputElement | null>(null);
const isDragOver = ref(false);
let dragLeaveTimer: ReturnType<typeof setTimeout> | null = null;

const { fitExtent } = useMap();
const { mapViewMode } = useFilterStore();
const { formatDateTime: formatDisplayDateTime } = useDateTimeSettings();
const geoJsonFormat = new GeoJSON();



/* ─── Date formatting ─── */
function formatDateTime(iso: string): string {
  return formatDisplayDateTime(iso);
}

function getOverpassCount(area: MonitoredArea): number | null {
  if (!area.coverage_geojson) return null;
  const doc = area.coverage_geojson as Record<string, unknown>;
  if (doc.type === 'FeatureCollection' && Array.isArray(doc.features)) {
    return (doc.features as unknown[]).length;
  }
  return 1;
}

/* ─── Pan to area ─── */
let panClickTimer: ReturnType<typeof setTimeout> | null = null;

function panToArea(area: MonitoredArea) {
  if (panClickTimer) clearTimeout(panClickTimer);
  panClickTimer = setTimeout(() => {
    panClickTimer = null;
    try {
      const features = geoJsonFormat.readFeatures(area.aoi_geojson, { featureProjection: 'EPSG:3857' });
      if (!features.length) return;
      let ext = features[0].getGeometry()?.getExtent()?.slice() as [number, number, number, number] | undefined;
      for (let i = 1; i < features.length; i++) {
        const e = features[i].getGeometry()?.getExtent();
        if (e && ext) {
          ext = [Math.min(ext[0], e[0]), Math.min(ext[1], e[1]), Math.max(ext[2], e[2]), Math.max(ext[3], e[3])];
        }
      }
      if (ext && isFinite(ext[0])) {
        mapViewMode.value = 'aoi';
        fitExtent(ext, [60, 60, 60, 60]);
      }
    } catch { /* ignore parse errors */ }
  }, 250);
}

const selectedAreas = ref<MonitorRow[]>([]);
const computingAreaIds = ref<string[]>([]);

function isAreaComputing(areaId: string): boolean {
  return computingAreaIds.value.includes(areaId);
}

async function computeAndApplyCoverage(areaId: string) {
  if (!computingAreaIds.value.includes(areaId)) {
    computingAreaIds.value = [...computingAreaIds.value, areaId];
  }
  try {
    const result = await submitCoverage();
    if (!result) return;

    setAreaCoverage(
      areaId,
      result.geojson,
      result.summary,
      result.queriedAt
    );
    await nextTick();
    if (typeof window !== 'undefined') {
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    }
  } finally {
    computingAreaIds.value = computingAreaIds.value.filter((id) => id !== areaId);
  }
}

async function waitForHydration(): Promise<void> {
  if (isHydrated.value) return;

  await new Promise<void>((resolve) => {
    const stop = watch(isHydrated, (hydrated) => {
      if (!hydrated) return;
      stop();
      resolve();
    });
  });
}

async function processFile(file: File) {
  await waitForHydration();
  const loaded = await loadAoiFile(file);
  if (!loaded) return;

  const rawName = loaded.file.name.replace(/\.(geo)?json$/i, '') || 'Uploaded AOI';
  const area = addAreaFromAoi(rawName, loaded.geojson);
  selectedAreas.value = [area, ...selectedAreas.value];
  await computeAndApplyCoverage(area.id);
}

async function onFileChange(evt: Event) {
  const input = evt.target as HTMLInputElement;
  const files = input.files;
  if (!files?.length) return;
  for (const file of Array.from(files)) {
    await processFile(file);
  }
  input.value = '';
}

/* ─── Drag & drop ─── */
function onDragOver() {
  if (dragLeaveTimer) { clearTimeout(dragLeaveTimer); dragLeaveTimer = null; }
  isDragOver.value = true;
}

function onDragLeave() {
  if (dragLeaveTimer) clearTimeout(dragLeaveTimer);
  dragLeaveTimer = setTimeout(() => { isDragOver.value = false; }, 100);
}

async function onDrop(evt: DragEvent) {
  isDragOver.value = false;
  const files = evt.dataTransfer?.files;
  if (!files?.length) return;
  for (const file of Array.from(files)) {
    if (file.name.endsWith('.json') || file.name.endsWith('.geojson')) {
      await processFile(file);
    }
  }
}

function openFilePicker(): void {
  fileInput.value?.click();
}

function onOpenPickerRequest(): void {
  openFilePicker();
}

function deleteSelected() {
  const ids = selectedAreas.value.map((area) => area.id);
  if (ids.length === 0) return;
  for (const id of ids) {
    removeArea(id);
  }
  selectedAreas.value = [];
  clearResult();
}

/* ─── Inline rename ─── */
const renamingAreaId = ref<string | null>(null);
const renameValue = ref('');
const renameInput = ref<HTMLInputElement | null>(null);

function startRename(data: MonitorRow) {
  if (panClickTimer) {
    clearTimeout(panClickTimer);
    panClickTimer = null;
  }
  renamingAreaId.value = data.id;
  renameValue.value = data.name;
  nextTick(() => {
    renameInput.value?.focus();
    renameInput.value?.select();
  });
}

function confirmRename(areaId: string) {
  const trimmed = renameValue.value.trim();
  if (trimmed && renamingAreaId.value === areaId) {
    renameArea(areaId, trimmed);
  }
  renamingAreaId.value = null;
}

function cancelRename() {
  renamingAreaId.value = null;
}

/* ─── Export ─── */
function exportSelected() {
  const toExport = selectedAreas.value.filter((a) => a.coverage_geojson);
  if (!toExport.length) return;

  // Merge into a single FeatureCollection
  const allFeatures: unknown[] = [];
  for (const area of toExport) {
    const doc = area.coverage_geojson as Record<string, unknown>;
    if (doc.type === 'FeatureCollection' && Array.isArray(doc.features)) {
      allFeatures.push(...doc.features);
    } else {
      allFeatures.push(doc);
    }
  }

  const merged = {
    type: 'FeatureCollection',
    features: allFeatures,
  };

  const blob = new Blob([JSON.stringify(merged, null, 2)], { type: 'application/geo+json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `coverage_export_${new Date().toISOString().slice(0, 10)}.geojson`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ─── Selection ↔ visibility sync ─── */
watch(areas, (nextAreas) => {
  isSyncingSelection.value = true;
  selectedAreas.value = nextAreas.filter((area) => area.visible);
}, { immediate: true, deep: true });

watch(selectedAreas, (selection) => {
  if (isSyncingSelection.value) {
    isSyncingSelection.value = false;
    return;
  }
  setVisibleAreaIds(selection.map((area) => area.id));
}, { deep: true });

onMounted(() => {
  window.addEventListener('aoi:open-file-picker', onOpenPickerRequest);
});

onBeforeUnmount(() => {
  window.removeEventListener('aoi:open-file-picker', onOpenPickerRequest);
});


</script>

<style scoped lang="scss">
.map-kit-monitor-panel--embedded {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: transparent;
}

.map-kit-monitor-panel > .map-kit-panel-body,
.map-kit-monitor-panel-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  padding: 0.15rem 0.15rem 1rem;
}

/* ─── Drop zone ─── */
.map-kit-monitor-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  border: 2px dashed var(--p-content-border-color, #d1d5db);
  border-radius: 10px;
  background: var(--p-content-background, #fff);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  text-align: center;
  user-select: none;

  &:hover {
    border-color: var(--p-primary-color, #00bfa5);
    background: rgba(0, 191, 165, 0.04);
  }

  &--empty {
    padding: 2rem 1.5rem;
    gap: 0.4rem;
  }

  &--dragover {
    border-color: var(--p-primary-color, #00bfa5);
    background: rgba(0, 191, 165, 0.08);
    box-shadow: 0 0 0 3px rgba(0, 191, 165, 0.12);
  }
}

.map-kit-monitor-dropzone-icon {
  font-size: 1.6rem;
  color: var(--p-primary-color, #00bfa5);
  opacity: 0.7;

  .map-kit-monitor-dropzone--empty & {
    font-size: 2.2rem;
    opacity: 0.85;
    margin-bottom: 0.25rem;
  }
}

.map-kit-monitor-dropzone-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--p-text-color, #1e293b);

  .map-kit-monitor-dropzone--empty & {
    font-size: 1rem;
  }
}

.map-kit-monitor-dropzone-subtitle {
  margin: 0;
  font-size: 0.82rem;
  color: var(--p-text-muted-color, #64748b);

  .map-kit-monitor-dropzone--empty & {
    font-size: 0.88rem;
    margin-top: 0.15rem;
  }
}

.map-kit-monitor-dropzone-hint {
  margin: 0.25rem 0 0;
  font-size: 0.72rem;
  color: var(--p-text-muted-color, #94a3b8);

  .map-kit-monitor-dropzone--empty & {
    font-size: 0.78rem;
    margin-top: 0.5rem;
  }
}

.map-kit-monitor-dropzone-error {
  margin: -0.35rem 0 0.75rem;
  font-size: 0.75rem;
  color: var(--p-red-500, #ef4444);
}

.map-kit-monitor-dropzone-notice {
  margin: -0.35rem 0 0.75rem;
  font-size: 0.75rem;
  color: var(--p-primary-color, #00bfa5);
}

.map-kit-monitor-dropzone-notice--loading {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.map-kit-monitor-table {
  flex: 1 1 auto;
  min-height: 0;
}

.map-kit-monitor-table :deep(.p-datatable-table-container) {
  min-height: 0;
}

.map-kit-monitor-table :deep(.p-paginator) {
  flex-shrink: 0;
}

.map-kit-monitor-table :deep(.p-datatable-tbody > tr > td) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-kit-monitor-name-cell {
  cursor: default;
}

.map-kit-monitor-name-text {
  cursor: pointer;
  &:hover {
    text-decoration: underline dotted;
  }
}

.map-kit-monitor-rename-input {
  width: 100%;
  padding: 2px 4px;
  font-size: 0.82rem;
  border: 1px solid var(--p-primary-color, #00bfa5);
  border-radius: 3px;
  background: var(--p-content-background, #fff);
  color: var(--p-text-color, #1e293b);
  outline: none;
}

.map-kit-monitor-status-subtitle {
  display: block;
  font-size: 0.72rem;
  color: var(--p-text-muted-color, #64748b);
}

.map-kit-monitor-status-subtitle.is-computed {
  color: var(--p-primary-color, #00bfa5);
}

.map-kit-monitor-coverage-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.map-kit-monitor-coverage-loading {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  color: var(--p-text-muted-color, #64748b);
}

.map-kit-monitor-coverage-dots {
  min-width: 1.2rem;
  text-align: left;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.map-kit-monitor-coverage-status {
  font-size: 0.72rem;
}

.map-kit-monitor-coverage-text {
  font-size: 0.78rem;
  color: var(--p-text-color, #64748b);
}

.map-kit-monitor-bulk {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.map-kit-monitor-bulk-count {
  font-size: 0.78rem;
  font-weight: 600;
}
</style>
